<template>
  <v-container fluid>
    <v-row align="center" justify="space-between" class="mb-4">
      <v-col cols="12" md="8">
        <h2 class="text-h5 font-weight-bold">Editar presupuesto</h2>
        <div v-if="presupuesto" class="caption grey--text">{{ presupuesto.numero_display }}</div>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-btn text color="primary" @click="$router.back()">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col cols="12">
        <v-skeleton-loader type="article" />
      </v-col>
    </v-row>

    <template v-else-if="presupuesto">
      <v-row>
        <v-col cols="12" md="4">
          <v-card class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold">Cliente</v-card-title>
            <v-card-text>
              <div class="font-weight-medium">{{ presupuesto.cliente_nombre }}</div>
            </v-card-text>
          </v-card>

          <v-card class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold">Opciones</v-card-title>
            <v-card-text>
              <v-text-field
                v-model="descuento"
                type="number"
                min="0"
                max="100"
                label="Descuento (%)"
                outlined
                dense
                suffix="%"
              />
              <v-text-field
                v-model="fechaVencimiento"
                type="date"
                label="Vencimiento"
                outlined
                dense
              />
            </v-card-text>
          </v-card>

          <v-card>
            <v-card-title class="subtitle-1 font-weight-bold">Observaciones</v-card-title>
            <v-card-text>
              <v-textarea
                v-model="observaciones"
                outlined
                dense
                rows="3"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="8">
          <v-card class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold">
              Productos
              <v-spacer />
              <v-btn small color="primary" @click="dialogProducto = true">
                <v-icon left small>mdi-plus</v-icon>
                Agregar producto
              </v-btn>
            </v-card-title>
          </v-card>

          <v-card>
            <v-card-title class="subtitle-1 font-weight-bold">Detalle</v-card-title>
            <v-card-text>
              <v-simple-table v-if="detalles.length > 0">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Descripción</th>
                      <th class="text-right" style="width: 130px">Cantidad</th>
                      <th style="width: 50px"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(d, i) in detalles" :key="i">
                      <td>{{ d.descripcion }}</td>
                      <td class="text-right">
                        <v-text-field
                          v-model.number="d.cantidad"
                          type="number"
                          min="0.01"
                          dense
                          outlined
                          hide-details
                          style="width: 100px"
                          class="ml-auto"
                        />
                      </td>
                      <td>
                        <v-btn icon small color="error" @click="quitarDetalle(i)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-alert v-else type="info" outlined dense>
                Sin productos. Agregue al menos uno.
              </v-alert>
            </v-card-text>

            <v-card-actions class="pa-4">
              <v-spacer />
              <v-btn
                color="success"
                :disabled="detalles.length === 0"
                :loading="loadingGuardar"
                @click="guardar"
              >
                <v-icon left>mdi-content-save</v-icon>
                Guardar cambios
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Dialog selector de producto -->
    <v-dialog v-if="dialogProducto" v-model="dialogProducto" persistent width="1000px">
    <v-card class="rounded-lg">
      <v-card-title class="headline">
        <v-icon left>mdi-package-variant-closed</v-icon>
        Seleccionar producto
        <v-spacer />
        <v-btn icon @click="dialogProducto = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-container class="pa-4">
        <ProductoSelector
          :showSuggestions="false"
          @process-producto="agregarProducto"
          @close-dialog="dialogProducto = false"
        />
      </v-container>
    </v-card>
  </v-dialog>
</v-container>
</template>

<script>
import { mapActions } from 'vuex';
import ProductoSelector from '@/components/shared/comprobantes/detalles/ProductoSelector.vue';

export default {
  name: 'PresupuestoEmpresaEditar',
  components: { ProductoSelector },
  data() {
    return {
      presupuesto: null,
      loading: false,
      detalles: [],
      descuento: '',
      fechaVencimiento: '',
      observaciones: '',
      loadingGuardar: false,
      dialogProducto: false,
    };
  },
  computed: {
    presupuestoId() {
      return this.$route.params.id;
    },
  },
  methods: {
    ...mapActions('presupuestoVenta', ['get_presupuesto', 'update_presupuesto']),

    async fetchPresupuesto() {
      this.loading = true;
      try {
        const res = await this.get_presupuesto(this.presupuestoId);
        this.presupuesto = res.data;
        this.descuento = res.data.descuento || '';
        this.fechaVencimiento = res.data.fecha_vencimiento || '';
        this.observaciones = res.data.observaciones || '';
        this.detalles = res.data.detalles.map(d => ({
          psp: d.psp,
          descripcion: d.descripcion,
          cantidad: parseFloat(d.cantidad),
        }));
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    agregarProducto(producto) {
      const existente = this.detalles.find(d => d.psp === producto.id);
      if (existente) {
        existente.cantidad = parseFloat(existente.cantidad) + 1;
      } else {
        this.detalles.push({ psp: producto.id, descripcion: producto.descripcion, cantidad: 1 });
      }
    },

    quitarDetalle(index) {
      this.detalles.splice(index, 1);
    },

    async guardar() {
      this.loadingGuardar = true;
      try {
        const payload = {
          cliente: this.presupuesto.cliente,
          observaciones: this.observaciones,
          detalles: this.detalles.map(d => ({ psp: d.psp, cantidad: String(d.cantidad) })),
        };
        if (this.descuento) payload.descuento = String(this.descuento);
        if (this.fechaVencimiento) payload.fecha_vencimiento = this.fechaVencimiento;

        await this.update_presupuesto({ id: this.presupuestoId, data: payload });
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'success',
          text: 'Presupuesto actualizado', icon: 'mdi-check', timeout: 2000,
        });
        this.$router.push({ name: 'empresa.ventas.presupuesto.consultar', params: { id: this.presupuestoId } });
      } catch (e) {
        const msg = e.response?.data?.error || 'Error al actualizar';
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'error',
          text: msg, icon: 'mdi-alert', timeout: 3500,
        });
      } finally {
        this.loadingGuardar = false;
      }
    },
  },
  created() {
    this.fetchPresupuesto();
  },
};
</script>
